import { AlertTriangle, Clock3, PieChart } from 'lucide-react';
import type { InvestmentAllocationHolding, InvestmentAllocationResponse } from 'shared';

import { formatCurrency } from '../../lib/format/currency';
import { Card } from '../ui/Card';

interface InvestmentAllocationCardProps {
  allocation: InvestmentAllocationResponse | undefined;
  isError: boolean;
  isLoading: boolean;
}

const UNAVAILABLE = 'Data unavailable';

const statusLabels = {
  BELOW_TARGET: 'Below target',
  NEAR_TARGET: 'Near target',
  ABOVE_TARGET: 'Above target',
  ABOVE_MAXIMUM: 'Above maximum',
} as const;

const statusClasses = {
  BELOW_TARGET: 'text-text',
  NEAR_TARGET: 'text-success',
  ABOVE_TARGET: 'text-warn',
  ABOVE_MAXIMUM: 'text-danger',
} as const;

const unmatchedLabels = {
  MATCHED: 'Thesis linked',
  THESIS_MISSING: 'Thesis missing',
  IDENTITY_AMBIGUOUS: 'Thesis missing: identity ambiguous',
  IDENTITY_REVIEW_REQUIRED: 'Thesis missing: identity confirmation required',
} as const;

function formatPct(value: string | null): string {
  if (value === null) return UNAVAILABLE;
  return `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(Number(value))}%`;
}

function formatPoints(value: string | null): string {
  if (value === null) return 'No comparison';
  const numeric = Number(value);
  const formatted = new Intl.NumberFormat('en-GB', {
    maximumFractionDigits: 2,
    signDisplay: 'exceptZero',
  }).format(numeric);
  return `${formatted} pp`;
}

function formatMoney(value: string | null, currency: string): string {
  return value === null ? UNAVAILABLE : formatCurrency(value, currency);
}

type ReportedAsOf = NonNullable<InvestmentAllocationResponse['valuation']['reportedAsOf']>;

function formatReportedAsOf(reported: ReportedAsOf): string {
  const [date = '', time = ''] = reported.localDateTime.split('T');
  // Formats the reported calendar date only; the clock time is shown exactly as HL printed it.
  const dateLabel = new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
  const zone =
    reported.resolution !== 'EXACT' ? 'UK time' : reported.utcOffsetMinutes === 60 ? 'BST' : 'GMT';
  return `${dateLabel}, ${time} ${zone}`;
}

function unavailableMessage(allocation: InvestmentAllocationResponse): string {
  switch (allocation.valuation.unavailableReason) {
    case 'UNSUPPORTED_SOURCE':
      return 'Allocation uses imported HL holdings snapshots. This account has no supported valuation source.';
    case 'NO_HOLDINGS_SNAPSHOT':
      return 'Import an HL holdings snapshot to calculate account value, cash and weights.';
    case 'CURRENCY_MISMATCH':
      return `The HL valuation is in ${allocation.valuation.sourceCurrency ?? 'another currency'} but this account uses ${allocation.account.currency}. No timestamped FX rate is stored for the snapshot, so values are not converted.`;
    case 'INCONSISTENT_TOTALS':
      return 'The HL snapshot does not reconcile: holdings plus cash do not equal the stated total value.';
    case 'NON_POSITIVE_ACCOUNT_VALUE':
      return 'Account value is zero or negative, so portfolio weights cannot be calculated.';
    default:
      return UNAVAILABLE;
  }
}

function comparisonText(holding: InvestmentAllocationHolding): string | null {
  switch (holding.comparisonGap) {
    case 'VALUATION_UNAVAILABLE':
      return UNAVAILABLE;
    case 'THESIS_UNMATCHED':
      return `${unmatchedLabels[holding.thesisMatchStatus]}: no target comparison`;
    case 'THESIS_NOT_ACTIVE':
      return `Thesis ${(holding.thesis?.status ?? 'inactive').replaceAll('_', ' ').toLowerCase()}: no target comparison`;
    case 'TARGET_UNSET':
      return 'Target unset: no target comparison';
    default:
      return null;
  }
}

function hasActiveThesis(holding: InvestmentAllocationHolding): boolean {
  return holding.thesis?.status === 'ACTIVE';
}

function HoldingRow({
  holding,
  currency,
}: {
  holding: InvestmentAllocationHolding;
  currency: string;
}) {
  const active = hasActiveThesis(holding);
  const comparison = comparisonText(holding);
  return (
    <li className="border-b border-border py-3 last:border-0">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="font-semibold">{holding.symbol}</div>
          <div className="truncate text-sm text-text-muted">{holding.stock}</div>
        </div>
        {holding.allocationStatus ? (
          <div className={`text-sm font-medium ${statusClasses[holding.allocationStatus]}`}>
            {statusLabels[holding.allocationStatus]}
          </div>
        ) : comparison ? (
          <div className="text-sm text-text-muted">{comparison}</div>
        ) : null}
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm tabular-nums sm:grid-cols-5">
        <div>
          <dt className="text-text-muted">Value</dt>
          <dd>{formatMoney(holding.value, currency)}</dd>
        </div>
        <div>
          <dt className="text-text-muted">Weight</dt>
          <dd>{formatPct(holding.weightPct)}</dd>
        </div>
        <div>
          <dt className="text-text-muted">Target</dt>
          <dd>
            {active
              ? holding.targetPct === null
                ? 'Unset'
                : formatPct(holding.targetPct)
              : 'No active thesis'}
          </dd>
        </div>
        <div>
          <dt className="text-text-muted">Maximum</dt>
          <dd>
            {active
              ? holding.maximumPct === null
                ? 'Unset'
                : formatPct(holding.maximumPct)
              : 'No active thesis'}
          </dd>
        </div>
        <div>
          <dt className="text-text-muted">Difference from target</dt>
          <dd>{formatPoints(holding.differenceFromTargetPct)}</dd>
        </div>
      </dl>
    </li>
  );
}

export function InvestmentAllocationCard({
  allocation,
  isError,
  isLoading,
}: InvestmentAllocationCardProps) {
  const belowTarget = allocation
    ? allocation.holdings.filter((holding) =>
        allocation.activeBelowTargetHoldingIds.includes(holding.id),
      )
    : [];
  const reportedAsOf = allocation?.valuation.reportedAsOf ?? null;
  return (
    <Card
      aria-label="Allocation"
      header={
        <div className="flex items-start gap-3">
          <PieChart aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">Allocation</h2>
            <p className="text-sm text-text-muted">
              Actual weights compared with active thesis targets. These are facts, not buy or sell
              recommendations.
            </p>
          </div>
        </div>
      }
    >
      {isLoading ? (
        <p className="text-sm text-text-muted">Loading allocation...</p>
      ) : isError || !allocation ? (
        <p className="text-sm text-danger">Could not load allocation.</p>
      ) : (
        <div className="space-y-5">
          {!allocation.valuation.available ? (
            <div className="flex items-start gap-2 text-sm text-warn" role="status">
              <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">{UNAVAILABLE}</p>
                <p className="mt-1 text-text-muted">{unavailableMessage(allocation)}</p>
              </div>
            </div>
          ) : null}

          <dl className="grid min-w-0 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="min-w-0">
              <dt className="text-sm text-text-muted">Account value</dt>
              <dd className="mt-1 break-words text-2xl font-semibold tabular-nums">
                {formatMoney(allocation.accountValue, allocation.account.currency)}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-sm text-text-muted">Holdings value</dt>
              <dd className="mt-1 break-words text-2xl font-semibold tabular-nums">
                {formatMoney(allocation.holdingsValue, allocation.account.currency)}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-sm text-text-muted">Total cash</dt>
              <dd className="mt-1 break-words text-2xl font-semibold tabular-nums">
                {formatMoney(allocation.cash, allocation.account.currency)}
              </dd>
              {allocation.cashWeightPct !== null ? (
                <dd className="text-sm tabular-nums text-text-muted">
                  {formatPct(allocation.cashWeightPct)} of account value
                </dd>
              ) : null}
            </div>
            <div className="min-w-0">
              <dt className="text-sm text-text-muted">Cash available for allocation</dt>
              <dd className="mt-1 break-words text-2xl font-semibold tabular-nums">
                {formatMoney(allocation.availableCash, allocation.account.currency)}
              </dd>
            </div>
          </dl>

          {reportedAsOf ? (
            <p className="text-sm text-text-muted">
              Source: HL holdings snapshot valued {formatReportedAsOf(reportedAsOf)} (
              {allocation.valuation.sourceCurrency}). Weights use account value including cash.
            </p>
          ) : null}
          {reportedAsOf && reportedAsOf.resolution !== 'EXACT' ? (
            <p className="flex items-start gap-2 text-sm text-warn">
              <Clock3 aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              HL reported this time during the UK clock change, so its exact instant is unknown.
              {allocation.valuation.stale === null
                ? ` Whether it is more than ${allocation.valuation.staleAfterDays} days old cannot be determined precisely.`
                : null}
            </p>
          ) : null}
          {allocation.valuation.stale ? (
            <p className="flex items-start gap-2 text-sm text-warn">
              <Clock3 aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              Valuation is more than {allocation.valuation.staleAfterDays} days old. Import a
              current HL holdings snapshot before relying on these weights.
            </p>
          ) : null}

          {allocation.valuation.available ? (
            <section aria-label="Active-thesis holdings below target">
              <h3 className="text-sm font-semibold">Active-thesis holdings below target</h3>
              {belowTarget.length > 0 ? (
                <ul className="mt-2 space-y-1 text-sm tabular-nums">
                  {belowTarget.map((holding) => (
                    <li key={holding.id}>
                      <span className="font-medium">{holding.symbol}</span>:{' '}
                      {formatPct(holding.weightPct)} vs {formatPct(holding.targetPct)} target (
                      {formatPoints(holding.differenceFromTargetPct)})
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-text-muted">
                  No active-thesis holdings are below target.
                </p>
              )}
            </section>
          ) : null}

          {allocation.holdings.length > 0 ? (
            <section aria-label="Holding allocation">
              <h3 className="text-sm font-semibold">Holdings</h3>
              <ul className="mt-1">
                {allocation.holdings.map((holding) => (
                  <HoldingRow
                    key={holding.id}
                    holding={holding}
                    currency={allocation.account.currency}
                  />
                ))}
              </ul>
            </section>
          ) : null}

          {allocation.unmatchedActiveTheses.length > 0 ? (
            <section aria-label="Active theses without a matched holding">
              <h3 className="text-sm font-semibold">Active theses without a matched holding</h3>
              <ul className="mt-2 space-y-1 text-sm">
                {allocation.unmatchedActiveTheses.map((thesis) => (
                  <li key={thesis.id}>
                    <span className="font-medium">{thesis.displaySymbol}</span>: target{' '}
                    {thesis.targetPct === null ? 'unset' : formatPct(thesis.targetPct)}, maximum{' '}
                    {thesis.maximumPct === null ? 'unset' : formatPct(thesis.maximumPct)}. Weight{' '}
                    {UNAVAILABLE.toLowerCase()}: no unambiguous holding in the latest snapshot.
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <p className="border-t border-border pt-3 text-xs text-text-muted">
            Near target means within ±{allocation.nearTargetTolerancePct} percentage point of the
            target, inclusive. Above maximum takes precedence and means strictly greater than the
            maximum.
          </p>
        </div>
      )}
    </Card>
  );
}
